import React, { useMemo, useState } from 'react';
import { Card, Descriptions, Table, Button, Space, Tag, App, Modal, Form, Select, InputNumber, Input, DatePicker, Popconfirm, Tooltip, Typography } from 'antd';
import { ArrowLeftOutlined, LinkOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supplierInquiryService, type SupplierInquiry, type SupplierInquiryLine } from '@/services/supplierInquiry.service';
import { supplierQuotationService, type CreateSupplierQuotationPayload } from '@/services/supplierQuotation.service';
import { bidAnalysisService } from '@/services/bidAnalysis.service';
import { purchaseOrderService } from '@/services/purchaseOrder.service';
import { projectLabel } from '@/utils/projectDisplay';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import {
  LINE_SPECIFICATIONS_MAX_LENGTH,
  LineSpecificationsText,
} from '@/components/procurement/LineSpecifications';
import { DOCUMENT_ENTITY_TYPE_SUPPLIER_INQUIRY } from '@/constants/document-entity-types';
import { DocumentRefLink } from '@/components/DocumentRefLink';

const SupplierInquiryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [creatingBid, setCreatingBid] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [form] = Form.useForm();

  const { data: inquiry, isLoading } = useQuery({
    queryKey: ['supplier-inquiry', id],
    queryFn: () => supplierInquiryService.getById(id!),
    enabled: !!id,
  });
  const { data: departments = [] } = useDepartmentsForSelection();

  const { data: quotations = [], isLoading: quotationsLoading } = useQuery({
    queryKey: ['supplier-quotations-by-inquiry', id],
    queryFn: () => supplierQuotationService.getByInquiry(id!),
    enabled: !!id,
  });

  const { data: linkedBidAnalyses = [] } = useQuery({
    queryKey: ['bid-analyses', 'by-inquiry', id],
    queryFn: () => bidAnalysisService.list({ supplier_inquiry_id: id! }),
    enabled: !!id,
    staleTime: 30000,
  });

  const { data: linkedPurchaseOrders = [] } = useQuery({
    queryKey: ['purchase-orders', 'by-inquiry', id],
    queryFn: () => purchaseOrderService.list({ supplier_inquiry_id: id! }),
    enabled: !!id,
    staleTime: 30000,
  });

  const linkedPurchaseRequests = useMemo(() => {
    const map = new Map<string, string>();
    const prLinks = (inquiry as any)?.prLinks as Array<{ purchase_requisition_id?: string; purchaseRequisition?: { id?: string; pr_number?: string } }> | undefined;
    for (const link of prLinks ?? []) {
      const prId = link.purchase_requisition_id || link.purchaseRequisition?.id;
      const prNumber = link.purchaseRequisition?.pr_number || '';
      if (prId) map.set(prId, prNumber);
    }
    for (const line of inquiry?.lines ?? []) {
      const prId = line.purchase_requisition_id;
      const prNumber = (line as any).purchaseRequisition?.pr_number || '';
      if (prId && !map.has(prId)) map.set(prId, prNumber);
    }
    return [...map.entries()].map(([prId, prNumber]) => ({ prId, prNumber }));
  }, [inquiry]);

  const deleteInquiryMutation = useMutation({
    mutationFn: () => supplierInquiryService.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-inquiries'] });
      message.success('Supplier inquiry deleted');
      navigate('/scm/procurement/supplier-inquiries');
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    },
  });

  const deleteQuotationMutation = useMutation({
    mutationFn: (quotationId: string) => supplierQuotationService.delete(quotationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-quotations-by-inquiry', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-quotations'] });
      message.success('Quotation deleted');
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    },
  });

  const copyLink = async (inquirySupplierId: string) => {
    try {
      const { link } = await supplierInquiryService.getLink(inquirySupplierId);
      await navigator.clipboard.writeText(link);
      message.success('Link copied to clipboard');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to get link');
    }
  };

  const openReceiveModal = () => {
    form.resetFields();
    form.setFieldsValue({
      department_id: inquiry?.department_id || undefined,
    });
    inquiry?.lines?.forEach((line: SupplierInquiryLine) => {
      form.setFieldValue([`qty_${line.id}`], line.quantity);
    });
    setReceiveModalOpen(true);
  };

  const updateInquiryStatus = async (newStatus: string) => {
    if (!id) return;
    setStatusUpdating(true);
    try {
      await supplierInquiryService.updateStatus(id, newStatus);
      message.success(`Inquiry marked as ${newStatus}`);
      queryClient.invalidateQueries({ queryKey: ['supplier-inquiry', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-inquiries'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const createBidAnalysis = async () => {
    if (!id) return;
    setCreatingBid(true);
    try {
      const created = await bidAnalysisService.create(id, inquiry?.department_id);
      message.success('Bid analysis created');
      queryClient.invalidateQueries({ queryKey: ['bid-analyses'] });
      navigate(`/scm/procurement/bid-analyses/${created.id}`);
    } catch (e: any) {
      const msg = e?.response?.data?.message || '';
      if (msg.includes('already exists')) {
        const list = await bidAnalysisService.list({ supplier_inquiry_id: id });
        if (list.length) navigate(`/scm/procurement/bid-analyses/${list[0].id}`);
        else message.error('Bid analysis already exists');
      } else message.error(msg || 'Failed to create bid analysis');
    } finally {
      setCreatingBid(false);
    }
  };

  const inquiryDeleteBlockedByQuotations = quotationsLoading || quotations.length > 0;

  const submitReceiveQuotation = async () => {
    const values = await form.validateFields();
    const supplier_inquiry_supplier_id = values.supplier_inquiry_supplier_id;
    if (!supplier_inquiry_supplier_id || !inquiry?.lines?.length) return;
    const lines = inquiry.lines.map((line: SupplierInquiryLine) => ({
      supplier_inquiry_line_id: line.id,
      unit_price: values[`price_${line.id}`] ?? 0,
      quoted_quantity: values[`qty_${line.id}`] ?? line.quantity,
      uom: line.uom,
      discount_percent: values[`discount_${line.id}`],
      lead_time_days: values[`lead_${line.id}`],
      promised_delivery_date: values[`delivery_${line.id}`]?.format?.('YYYY-MM-DD'),
      remarks: values[`remarks_${line.id}`],
      brand_offered: String(form.getFieldValue(`brand_${line.id}`) ?? '').trim() || null,
      compliance_notes: String(form.getFieldValue(`offered_spec_${line.id}`) ?? '').trim() || null,
    }));
    const payload: CreateSupplierQuotationPayload = {
      supplier_inquiry_supplier_id,
      supplier_quotation_ref: values.supplier_quotation_ref,
      quotation_date: values.quotation_date?.format?.('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD'),
      validity_date: values.validity_date?.format?.('YYYY-MM-DD'),
      payment_terms: values.payment_terms,
      delivery_terms: values.delivery_terms,
      remarks: values.remarks,
      department_id: values.department_id,
      lines,
    };
    try {
      await supplierQuotationService.createManual(payload);
      message.success('Quotation recorded');
      setReceiveModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['supplier-inquiry', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-quotations-by-inquiry', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-quotations'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to save quotation');
    }
  };

  if (!id || (isLoading && !inquiry)) return null;
  if (!inquiry) return <Card><p>Inquiry not found.</p></Card>;

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/supplier-inquiries')} style={{ marginBottom: 16 }}>
        Back
      </Button>
      <Card
        title={`Inquiry ${inquiry.inquiry_number}`}
        extra={
          <Space>
            <Tag color="blue">{inquiry.status}</Tag>
            {inquiry.status === 'Draft' && (
              <Button type="default" loading={statusUpdating} onClick={() => updateInquiryStatus('Sent')}>
                Mark as Sent
              </Button>
            )}
            <Button type="default" loading={creatingBid} onClick={createBidAnalysis}>
              Create bid analysis
            </Button>
            {inquiryDeleteBlockedByQuotations ? (
              <Tooltip title="Cannot delete: supplier quotation(s) exist against this inquiry. Delete those quotations first.">
                <span>
                  <Button danger icon={<DeleteOutlined />} disabled>
                    Delete inquiry
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Popconfirm
                title="Delete this inquiry?"
                description="Only allowed if there are no quotations, bid analyses, or purchase orders linked."
                okText="Delete"
                okButtonProps={{ danger: true }}
                disabled={deleteInquiryMutation.isPending}
                onConfirm={() => deleteInquiryMutation.mutate()}
              >
                <Button danger icon={<DeleteOutlined />} loading={deleteInquiryMutation.isPending}>
                  Delete inquiry
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Inquiry Date">{inquiry.inquiry_date ? dayjs(inquiry.inquiry_date).format('DD MMM YYYY') : '-'}</Descriptions.Item>
          <Descriptions.Item label="Response Deadline">{inquiry.response_deadline ? dayjs(inquiry.response_deadline).format('DD MMM YYYY') : '-'}</Descriptions.Item>
          <Descriptions.Item label="Currency">{inquiry.currency}</Descriptions.Item>
          <Descriptions.Item label="Department">
            {inquiry.department_id
              ? (() => {
                  const d = departments.find((x) => x.id === inquiry.department_id);
                  return d ? `${d.department_code} - ${d.department_name}` : inquiry.department_id;
                })()
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Remarks" span={2}>{inquiry.remarks || '-'}</Descriptions.Item>
        </Descriptions>
        {(linkedPurchaseRequests.length > 0 ||
          quotations.length > 0 ||
          linkedBidAnalyses.length > 0 ||
          linkedPurchaseOrders.length > 0) && (
          <div style={{ marginTop: 12 }}>
            <Typography.Text strong>Document links: </Typography.Text>
            <Space wrap size="small">
              {linkedPurchaseRequests.map(({ prId, prNumber }) => (
                <DocumentRefLink
                  key={prId}
                  to={`/scm/procurement/purchase-requests/${prId}`}
                  number={prNumber || undefined}
                  id={prId}
                />
              ))}
              {quotations.map((q: any) => (
                <DocumentRefLink
                  key={q.id}
                  to={`/scm/procurement/supplier-quotations/${q.id}`}
                  number={q.quotation_number}
                  id={q.id}
                />
              ))}
              {linkedBidAnalyses.map((ba) => (
                <DocumentRefLink
                  key={ba.id}
                  to={`/scm/procurement/bid-analyses/${ba.id}`}
                  number={ba.analysis_number}
                  id={ba.id}
                />
              ))}
              {linkedPurchaseOrders.map((po: any) => (
                <DocumentRefLink
                  key={po.id}
                  to={`/scm/procurement/purchase-orders/${po.id}`}
                  number={po.po_number}
                  id={po.id}
                />
              ))}
            </Space>
          </div>
        )}
        <h4 style={{ marginTop: 16 }}>Suppliers &amp; links</h4>
        <Space wrap style={{ marginBottom: 16 }}>
          {inquiry.inquirySuppliers?.map((ins) => (
            <Card key={ins.id} size="small" style={{ width: 280 }}>
              <strong>{(ins.supplier as any)?.supplier_name ?? ins.supplier_id}</strong>
              <br />
              <Tag>{ins.status}</Tag>
              <Button type="link" size="small" icon={<LinkOutlined />} onClick={() => copyLink(ins.id)}>Copy link</Button>
            </Card>
          ))}
        </Space>
        <h4>Lines</h4>
        <Table
          size="small"
          rowKey="id"
          dataSource={inquiry.lines ?? []}
          columns={[
            { title: '#', dataIndex: 'line_number', width: 50 },
            { title: 'Item', dataIndex: 'item_code', width: 100 },
            {
              title: 'Description',
              dataIndex: 'item_description',
              render: (desc: string, row: SupplierInquiryLine) => (
                <>
                  <div>{desc}</div>
                  <LineSpecificationsText value={row.specifications} />
                </>
              ),
            },
            { title: 'Qty', dataIndex: 'quantity', width: 80, align: 'right' },
            { title: 'UOM', dataIndex: 'uom', width: 60 },
            { title: 'Project', key: 'project', width: 120, render: (_: unknown, row: SupplierInquiryLine) => projectLabel(row) },
            { title: 'Required', dataIndex: 'required_date', width: 110, render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '-') },
          ]}
          pagination={false}
        />
      </Card>

      <Card
        title="Received quotations"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openReceiveModal}>
            Receive quotation
          </Button>
        }
        style={{ marginTop: 16 }}
      >
        {quotationsLoading ? (
          <p>Loading...</p>
        ) : quotations.length === 0 ? (
          <p>No quotations received yet.</p>
        ) : (
          <Table
            size="small"
            rowKey="id"
            dataSource={quotations}
            columns={[
              { title: 'Quotation #', dataIndex: 'quotation_number', width: 120 },
              { title: 'Supplier', key: 'supplier', render: (_: any, r: any) => (r.supplier?.supplier_name ?? r.supplier_id) },
              { title: 'Date', dataIndex: 'quotation_date', width: 110, render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '-') },
              { title: 'Status', dataIndex: 'status', width: 100 },
              {
                title: 'Actions',
                width: 180,
                render: (_: any, row: any) => (
                  <Space size="small">
                    <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/supplier-quotations/${row.id}`)}>
                      View
                    </Button>
                    <Popconfirm
                      title="Delete this quotation?"
                      description="Not allowed if used in bid analysis or on a purchase order."
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      disabled={deleteQuotationMutation.isPending}
                      onConfirm={() => deleteQuotationMutation.mutate(row.id)}
                    >
                      <Button type="link" size="small" danger loading={deleteQuotationMutation.isPending}>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
            pagination={false}
          />
        )}
      </Card>

      <ProcurementAttachmentsCard
        entityType={DOCUMENT_ENTITY_TYPE_SUPPLIER_INQUIRY}
        entityId={id}
        queryKeyPrefix={['documents', 'supplier-inquiry']}
      />

      <Modal
        title="Receive quotation (manual entry)"
        open={receiveModalOpen}
        onCancel={() => setReceiveModalOpen(false)}
        onOk={submitReceiveQuotation}
        width={900}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="supplier_inquiry_supplier_id" label="Supplier" rules={[{ required: true }]}>
            <Select
              placeholder="Select supplier"
              options={inquiry.inquirySuppliers?.map((ins) => ({
                value: ins.id,
                label: (ins.supplier as any)?.supplier_name ?? ins.supplier_id,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect style={{ width: 280 }} />
          </Form.Item>
          <Form.Item name="supplier_quotation_ref" label="Supplier ref">
            <Input placeholder="e.g. Q-2024-001" />
          </Form.Item>
          <Form.Item name="quotation_date" label="Quotation date" initialValue={dayjs()}>
            <DatePicker />
          </Form.Item>
          <Form.Item name="validity_date" label="Validity date">
            <DatePicker />
          </Form.Item>
          <Form.Item name="payment_terms" label="Payment terms">
            <Input placeholder="e.g. Net 30" />
          </Form.Item>
          <Form.Item name="delivery_terms" label="Delivery terms">
            <Input />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
          <h4>Line items</h4>
          <Table
            size="small"
            rowKey="id"
            dataSource={inquiry.lines ?? []}
            pagination={false}
            expandable={{
              defaultExpandAllRows: true,
              expandedRowRender: (r: SupplierInquiryLine) => (
                <Space direction="vertical" style={{ width: '100%' }} size={4}>
                  <LineSpecificationsText value={r.specifications} label="Required spec" />
                  <Form.Item name={`brand_${r.id}`} label="Brand / make offered" style={{ marginBottom: 4 }}>
                    <Input maxLength={255} style={{ maxWidth: 320 }} />
                  </Form.Item>
                  <Form.Item
                    name={`offered_spec_${r.id}`}
                    label="Offered specification / deviations"
                    style={{ marginBottom: 4 }}
                  >
                    <Input.TextArea
                      autoSize={{ minRows: 2, maxRows: 8 }}
                      maxLength={LINE_SPECIFICATIONS_MAX_LENGTH}
                      showCount
                      placeholder="Blank = supplier complies with the required specification"
                    />
                  </Form.Item>
                </Space>
              ),
            }}
            columns={[
              { title: 'Item', dataIndex: 'item_code', width: 90 },
              { title: 'Description', dataIndex: 'item_description', ellipsis: true },
              { title: 'Qty req', dataIndex: 'quantity', width: 70, align: 'right' },
              {
                title: 'Your qty',
                width: 90,
                render: (_: any, r: SupplierInquiryLine) => (
                  <Form.Item name={`qty_${r.id}`} noStyle initialValue={r.quantity}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                ),
              },
              {
                title: 'Unit price',
                width: 100,
                render: (_: any, r: SupplierInquiryLine) => (
                  <Form.Item name={`price_${r.id}`} rules={[{ required: true }]} noStyle>
                    <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                ),
              },
              {
                title: 'Disc %',
                width: 70,
                render: (_: any, r: SupplierInquiryLine) => (
                  <Form.Item name={`discount_${r.id}`} noStyle>
                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                ),
              },
              {
                title: 'Lead days',
                width: 80,
                render: (_: any, r: SupplierInquiryLine) => (
                  <Form.Item name={`lead_${r.id}`} noStyle>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                ),
              },
              {
                title: 'Delivery',
                width: 120,
                render: (_: any, r: SupplierInquiryLine) => (
                  <Form.Item name={`delivery_${r.id}`} noStyle>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                ),
              },
            ]}
          />
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierInquiryDetailPage;
